import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { NAV_LINKS } from "../../data/venpa";
import { LOGO_URL } from "../../data/brand";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const onLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Use dark glass when at top of landing page (over hero), else light glass
  const isDark = onLanding && !scrolled;

  return (
    <header className="fixed top-4 inset-x-0 z-50 px-4 md:px-8" data-testid="site-nav">
      <div
        className={`relative mx-auto max-w-7xl rounded-full flex items-center justify-between px-4 md:px-6 py-2.5 transition-colors duration-300 ${
          isDark ? "glass-dark" : "glass-light-strong"
        }`}
      >
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <img src={LOGO_URL} alt="Venpa AI" className="w-9 h-9 group-hover:rotate-6 transition-transform" />
          <span className={`display-md text-xl md:text-2xl ${isDark ? "text-white" : "text-ink-soft"}`}>
            Venpa <span className={isDark ? "text-white/55" : "text-ink-soft/55"}>AI</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={onLanding ? l.href : `/${l.href}`}
              data-testid={`nav-link-${l.href.replace('#','')}`}
              className={`px-3.5 py-2 rounded-full text-sm font-semibold transition-all ${
                isDark ? "text-white/80 hover:text-white hover:bg-white/10" : "text-ink-soft/80 hover:text-ink-soft hover:bg-white/60"
              }`}
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/blog"
            data-testid="nav-link-blog"
            className={`px-3.5 py-2 rounded-full text-sm font-semibold transition-all ${
              isDark ? "text-white/80 hover:text-white hover:bg-white/10" : "text-ink-soft/80 hover:text-ink-soft hover:bg-white/60"
            }`}
          >
            Blog
          </Link>
          <Link
            to="/about"
            data-testid="nav-link-about"
            className={`px-3.5 py-2 rounded-full text-sm font-semibold transition-all ${
              isDark ? "text-white/80 hover:text-white hover:bg-white/10" : "text-ink-soft/80 hover:text-ink-soft hover:bg-white/60"
            }`}
          >
            About
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <a href={onLanding ? "#contact" : "/#contact"} className="hidden md:inline-flex btn-brand !py-2.5 !px-5 text-sm" data-testid="nav-cta-book">
            Book a Demo
          </a>
          <button
            className={`md:hidden grid place-items-center w-10 h-10 rounded-full transition-colors ${isDark ? "bg-white/10 text-white border border-white/20" : "bg-white/70 text-ink-soft border border-white/60"}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden mt-3 mx-auto max-w-7xl rounded-3xl glass-light-strong p-4 flex flex-col gap-1" data-testid="nav-mobile-menu">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={onLanding ? l.href : `/${l.href}`} onClick={() => setOpen(false)} className="px-4 py-3 rounded-2xl font-semibold text-ink-soft hover:bg-white/60">{l.label}</a>
          ))}
          <Link to="/blog" onClick={() => setOpen(false)} className="px-4 py-3 rounded-2xl font-semibold text-ink-soft hover:bg-white/60">Blog</Link>
          <Link to="/about" onClick={() => setOpen(false)} className="px-4 py-3 rounded-2xl font-semibold text-ink-soft hover:bg-white/60">About</Link>
          <a href={onLanding ? "#contact" : "/#contact"} onClick={() => setOpen(false)} className="btn-brand mt-2 justify-center">Book a Demo</a>
        </div>
      )}
    </header>
  );
}
